import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Calendar, Clock, User, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogsPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/blogs`);
      const blogs = response.data.map((blog, index) => ({
        id: blog.id,
        slug: blog.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        title: blog.title,
        excerpt: blog.excerpt,
        author: blog.author,
        date: new Date(blog.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        readTime: blog.readTime,
        category: blog.category,
        image: ['from-blue-500 to-cyan-500', 'from-green-500 to-emerald-500', 'from-purple-500 to-pink-500', 'from-orange-500 to-red-500', 'from-indigo-500 to-blue-500'][index % 5],
        content: blog.content
      }));
      setBlogPosts(blogs);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const blogPostsOld = [
    {
      id: 1,
      slug: "ai-seo-automation-guide-2025",
      title: "The Complete Guide to AI-Powered SEO Automation in 2025",
      excerpt: "Discover how artificial intelligence is revolutionizing SEO strategies and helping businesses achieve top rankings with zero manual effort. Learn the latest AI techniques that are transforming digital marketing.",
      author: "Rahul Sharma",
      date: "January 15, 2025",
      readTime: "8 min read",
      category: "SEO Automation",
      image: "from-blue-500 to-cyan-500",
      content: `
        <h2>Introduction to AI SEO Automation</h2>
        <p>The landscape of Search Engine Optimization has undergone a dramatic transformation with the integration of artificial intelligence. In 2025, AI-powered SEO automation isn't just a luxury—it's a necessity for businesses that want to stay competitive in the digital marketplace.</p>
        
        <h2>Why Traditional SEO is Becoming Obsolete</h2>
        <p>Manual SEO processes that once took marketing teams 40+ hours per week can now be completed in minutes by AI systems. From keyword research to content optimization, backlink analysis to technical SEO audits, AI is handling tasks with unprecedented speed and accuracy.</p>
        
        <h2>Key Benefits of AI SEO Automation</h2>
        <ul>
          <li><strong>Time Savings:</strong> Reduce manual SEO work by 95%</li>
          <li><strong>Better Rankings:</strong> AI analyzes millions of ranking factors in real-time</li>
          <li><strong>Cost Efficiency:</strong> Save ₹50,000+ monthly on SEO agencies</li>
          <li><strong>Continuous Optimization:</strong> AI works 24/7 improving your rankings</li>
          <li><strong>Predictive Analysis:</strong> AI forecasts search trends before they happen</li>
        </ul>
        
        <h2>How AI SEO Works</h2>
        <p>Modern AI SEO systems use machine learning algorithms to analyze your website, competitors, and search engine patterns. They automatically implement optimizations including meta tags, content structure, internal linking, and technical improvements.</p>
        
        <h2>Real-World Results</h2>
        <p>Businesses using AI SEO automation are seeing 300% increases in organic traffic within 90 days, with some achieving first-page rankings for competitive keywords in as little as 30 days.</p>
        
        <h2>Getting Started with AI SEO</h2>
        <p>The best part? Implementation takes just 5 minutes. Connect your website once, and AI handles everything from weekly audits to automatic optimizations. The future of SEO is here, and it's fully automated.</p>
      `
    },
    {
      id: 2,
      slug: "local-seo-google-business-optimization",
      title: "How to Dominate Local Search: Google Business Profile Optimization Guide",
      excerpt: "Master local SEO and watch your business appear at the top of 'near me' searches. This comprehensive guide reveals proven strategies to optimize your Google Business Profile and attract more local customers.",
      author: "Priya Mehta",
      date: "January 12, 2025",
      readTime: "10 min read",
      category: "Local SEO",
      image: "from-green-500 to-emerald-500",
      content: `
        <h2>The Power of Local Search</h2>
        <p>Did you know that 46% of all Google searches are local? If your business isn't optimized for local search, you're missing out on thousands of potential customers who are actively looking for services like yours right now.</p>
        
        <h2>Google Business Profile: Your Digital Storefront</h2>
        <p>Your Google Business Profile (formerly Google My Business) is often the first thing potential customers see when searching for local businesses. It's more important than your website for local visibility.</p>
        
        <h2>Essential Optimization Steps</h2>
        <ul>
          <li><strong>Complete Profile Information:</strong> Fill out every field, including hours, services, and attributes</li>
          <li><strong>Professional Photos:</strong> Upload high-quality images of your business, products, and team</li>
          <li><strong>Regular Posts:</strong> Share updates, offers, and news at least 2-3 times per week</li>
          <li><strong>Review Management:</strong> Respond to all reviews within 24 hours</li>
          <li><strong>Accurate NAP:</strong> Ensure Name, Address, Phone are consistent everywhere</li>
        </ul>
        
        <h2>The Review Factor</h2>
        <p>Reviews account for 15% of local search ranking factors. Businesses with 5+ reviews per month see a 35% increase in local visibility. AI can automate review requests and responses, ensuring you never miss an opportunity.</p>
        
        <h2>Local SEO Beyond Google Business</h2>
        <p>While Google Business is crucial, complete local SEO includes citation building, local link building, and location-specific content creation. AI automation handles all these tasks simultaneously.</p>
        
        <h2>Measuring Local SEO Success</h2>
        <p>Track metrics like local pack rankings, direction requests, phone calls, and website visits from your Google Business Profile. Most businesses see measurable improvements within 30 days.</p>
      `
    },
    {
      id: 3,
      slug: "social-media-automation-strategy-2025",
      title: "Social Media Automation: Post 10X More Without Spending More Time",
      excerpt: "Learn how to scale your social media presence across Instagram, Facebook, LinkedIn, YouTube, and X (Twitter) using AI automation. Discover the secrets to consistent posting without the overwhelm.",
      author: "Amit Kumar",
      date: "January 10, 2025",
      readTime: "7 min read",
      category: "Social Media",
      image: "from-purple-500 to-pink-500",
      content: `
        <h2>The Social Media Dilemma</h2>
        <p>Every business knows they need an active social media presence, but the reality is brutal: creating quality content for multiple platforms takes 20+ hours per week. That's a full half-time position just for social media.</p>
        
        <h2>The AI Solution</h2>
        <p>AI-powered social media automation changes everything. Instead of spending hours brainstorming, creating, and scheduling posts, AI generates platform-optimized content in seconds and posts it at the perfect times for maximum engagement.</p>
        
        <h2>Multi-Platform Strategy</h2>
        <p>Different platforms require different approaches:</p>
        <ul>
          <li><strong>Instagram:</strong> Visual-first content with engaging captions and hashtags</li>
          <li><strong>Facebook:</strong> Community-building posts with high engagement potential</li>
          <li><strong>LinkedIn:</strong> Professional insights and industry thought leadership</li>
          <li><strong>YouTube:</strong> Video content and shorts optimized for watch time</li>
          <li><strong>X (Twitter):</strong> Real-time updates and trending topic engagement</li>
        </ul>
        
        <h2>Content Variety is Key</h2>
        <p>AI ensures your content mix includes educational posts, promotional content, user-generated content reposts, industry news, behind-the-scenes glimpses, and engagement-driving questions—all balanced automatically.</p>
        
        <h2>Timing and Frequency</h2>
        <p>AI analyzes when your audience is most active and schedules posts accordingly. Most successful businesses post 5-10 times per week across all platforms, something nearly impossible to maintain manually.</p>
        
        <h2>Results You Can Expect</h2>
        <p>Businesses using AI social media automation see average engagement increases of 250% and follower growth of 40% per month. The key is consistency—something only automation can guarantee.</p>
      `
    },
    {
      id: 4,
      slug: "ai-content-creation-dall-e-runwayml",
      title: "Creating Stunning Marketing Content with AI: DALL-E 3 & RunwayML Guide",
      excerpt: "Explore how AI is democratizing professional content creation. Learn to generate magazine-quality images and cinematic videos using DALL-E 3 and RunwayML Gen-3—no design skills required.",
      author: "Sneha Patel",
      date: "January 8, 2025",
      readTime: "9 min read",
      category: "AI Content",
      image: "from-orange-500 to-red-500",
      content: `
        <h2>The Content Creation Revolution</h2>
        <p>Professional marketing content that once required expensive designers and video production teams can now be created by AI in minutes. We're living in an era where technology has democratized creativity.</p>
        
        <h2>DALL-E 3: AI Image Generation</h2>
        <p>DALL-E 3, OpenAI's latest image generation model, produces photorealistic images from text descriptions. Whether you need product photos, social media graphics, blog headers, or advertising visuals—AI creates them on demand.</p>
        
        <h2>Creating Effective Image Prompts</h2>
        <p>The key to great AI images is detailed prompts. Instead of "coffee shop," try "modern minimalist coffee shop interior with warm lighting, large windows, customers working on laptops, professional photography style, wide angle."</p>
        
        <h2>RunwayML Gen-3: Cinematic AI Videos</h2>
        <p>Video content gets 1200% more engagement than text and images combined. RunwayML Gen-3 creates professional video content including product demos, explainer videos, and social media reels—all from simple text descriptions.</p>
        
        <h2>Content Consistency</h2>
        <p>AI ensures brand consistency across all visual content. Define your brand style once, and AI generates all future images and videos matching that aesthetic perfectly.</p>
        
        <h2>Cost Savings</h2>
        <p>Traditional content creation costs ₹5,000-10,000 per image and ₹50,000+ per video. AI reduces these costs by 95% while maintaining professional quality.</p>
        
        <h2>Weekly Automation</h2>
        <p>Smart businesses automate content creation completely. AI generates 2 professional images and 2 videos every week, ensuring a constant flow of fresh marketing materials without any manual effort.</p>
      `
    },
    {
      id: 5,
      slug: "marketing-automation-roi-calculator",
      title: "Marketing Automation ROI: How to Calculate If You're Getting Real Value",
      excerpt: "Stop guessing and start measuring. This comprehensive guide shows you exactly how to calculate your marketing automation ROI and prove the value of your investment to stakeholders.",
      author: "Rajesh Verma",
      date: "January 5, 2025",
      readTime: "6 min read",
      category: "Strategy",
      image: "from-yellow-500 to-orange-500",
      content: `
        <h2>Understanding Marketing Automation ROI</h2>
        <p>Return on Investment (ROI) is the ultimate metric for evaluating any business decision. When it comes to marketing automation, calculating true ROI goes beyond simple cost savings—it includes time saved, efficiency gains, and revenue growth.</p>
        
        <h2>The ROI Formula</h2>
        <p>ROI = (Gains - Investment) / Investment × 100</p>
        <p>For marketing automation, your gains include cost savings, time savings (valued at hourly rates), increased revenue from better marketing, and efficiency improvements.</p>
        
        <h2>Calculating Direct Cost Savings</h2>
        <ul>
          <li><strong>Agency Fees Eliminated:</strong> ₹50,000-100,000/month saved</li>
          <li><strong>Staff Time Saved:</strong> 40+ hours/week at ₹500-1000/hour</li>
          <li><strong>Tool Consolidation:</strong> Replace 5-10 different tools with one platform</li>
          <li><strong>Content Creation Costs:</strong> 95% reduction in design and video expenses</li>
        </ul>
        
        <h2>Measuring Indirect Gains</h2>
        <p>Time savings allow your team to focus on high-value activities like strategy and business development. This often results in 2-3x more new business opportunities being pursued.</p>
        
        <h2>Revenue Attribution</h2>
        <p>Track how automated marketing contributes to revenue through increased website traffic, lead generation, and conversion improvements. Most businesses see 300% traffic increases within 90 days.</p>
        
        <h2>Real-World Example</h2>
        <p>A typical small business paying ₹3,999/month for automation saves ₹80,000+ in agency fees and 160 hours monthly in staff time. That's an ROI of over 2000% in the first year alone.</p>
        
        <h2>Start Tracking Today</h2>
        <p>Set baseline metrics before implementing automation, then track monthly improvements. Document everything—stakeholders love seeing concrete numbers proving value.</p>
      `
    },
    {
      id: 6,
      slug: "white-label-saas-agency-guide",
      title: "Building a Profitable White-Label SaaS Agency: Complete Playbook",
      excerpt: "Transform your agency with white-label automation. Discover how to add recurring revenue streams, scale without hiring, and deliver enterprise-grade services under your own brand.",
      author: "Vikram Singh",
      date: "January 3, 2025",
      readTime: "12 min read",
      category: "Agency Growth",
      image: "from-violet-500 to-purple-500",
      content: `
        <h2>The Agency Evolution</h2>
        <p>Traditional agencies face a fundamental problem: growth requires hiring more people. White-label SaaS flips this model, allowing agencies to scale infinitely without proportional cost increases.</p>
        
        <h2>What is White-Label Automation?</h2>
        <p>White-label means reselling a powerful platform under your own brand. Your clients see your branding, your domain, your reports—they never know you're using someone else's technology. It's like having an entire tech team without the overhead.</p>
        
        <h2>Revenue Model Transformation</h2>
        <p>Move from project-based revenue (one-time) to recurring revenue (monthly). A single client at ₹3,999/month provides ₹47,988 annually—automatically, without additional work after setup.</p>
        
        <h2>Tiered Pricing Strategy</h2>
        <ul>
          <li><strong>1-50 clients:</strong> ₹1,999 per client/month = up to ₹99,950/month</li>
          <li><strong>51-150 clients:</strong> ₹1,899 per client/month = up to ₹2,84,850/month</li>
          <li><strong>151+ clients:</strong> ₹1,699 per client/month = unlimited revenue potential</li>
        </ul>
        
        <h2>Your Margins</h2>
        <p>If you charge clients ₹5,999/month and pay ₹1,999 for white-label service, you keep ₹4,000 per client. At 100 clients, that's ₹4,00,000 monthly profit—all passive income.</p>
        
        <h2>Client Onboarding</h2>
        <p>With automation, client onboarding takes 15 minutes. Connect their accounts, configure settings, and automation begins immediately. Scale from 10 to 100 clients without increasing staff.</p>
        
        <h2>Competitive Advantage</h2>
        <p>Offer services traditional agencies can't match: 24/7 optimization, instant reporting, consistent execution across all clients, and enterprise-grade technology at small business prices.</p>
        
        <h2>Getting Started</h2>
        <p>Start with 5-10 existing clients, prove results, then scale. Most agencies reach 50+ clients within 6 months using white-label automation.</p>
      `
    },
    {
      id: 7,
      slug: "seo-keyword-research-ai-2025",
      title: "AI-Powered Keyword Research: Find Hidden Opportunities Your Competitors Miss",
      excerpt: "Traditional keyword research tools show you the same data as everyone else. Discover how AI uncovers untapped keyword opportunities, predicts trending searches, and identifies gaps in your content strategy.",
      author: "Neha Gupta",
      date: "December 28, 2024",
      readTime: "8 min read",
      category: "SEO Strategy",
      image: "from-blue-500 to-purple-500",
      content: `
        <h2>Beyond Traditional Keyword Research</h2>
        <p>Every marketer uses the same keyword tools, finds the same keywords, and creates similar content. This creates a crowded battlefield where ranking becomes nearly impossible. AI changes this game entirely.</p>
        
        <h2>How AI Finds Hidden Keywords</h2>
        <p>AI analyzes patterns across millions of searches, identifies emerging trends before they appear in traditional tools, and discovers long-tail variations that human researchers would never find manually.</p>
        
        <h2>Intent Analysis</h2>
        <p>AI doesn't just find keywords—it understands search intent. It categorizes keywords by intent (informational, navigational, transactional, commercial) and matches them to the right content types for maximum conversion.</p>
        
        <h2>Competitive Gap Analysis</h2>
        <p>AI compares your keyword coverage against competitors, identifies topics where you have zero presence but competitors rank well, and prioritizes keywords based on difficulty vs. potential traffic.</p>
        
        <h2>Predictive Keyword Trends</h2>
        <p>The real power of AI is prediction. By analyzing search patterns, social media trends, and market signals, AI identifies keywords that will trend in the next 30-90 days—before your competitors even know they exist.</p>
        
        <h2>Automated Keyword Clustering</h2>
        <p>AI groups related keywords into topic clusters, ensuring your content strategy covers all variations of a topic comprehensively rather than creating redundant pages competing against each other.</p>
        
        <h2>Implementation Strategy</h2>
        <p>AI doesn't just research—it executes. Once keywords are identified, AI creates optimized content automatically, implements on-page SEO, and builds internal linking structures. Research to ranking happens automatically.</p>
        
        <h2>Results Timeline</h2>
        <p>Most businesses see first-page rankings for 5-10 new keywords within 30 days, with cumulative growth accelerating month over month as AI continues finding and targeting new opportunities.</p>
      `
    },
    {
      id: 8,
      slug: "instagram-growth-automation-2025",
      title: "Instagram Growth in 2025: How to Gain 1000+ Engaged Followers Per Month",
      excerpt: "Instagram's algorithm has changed, but growth is still possible—if you know the new rules. Learn the exact automation strategies that are working right now to build engaged, authentic Instagram audiences.",
      author: "Arjun Reddy",
      date: "December 25, 2024",
      readTime: "10 min read",
      category: "Social Growth",
      image: "from-pink-500 to-rose-500",
      content: `
        <h2>Instagram in 2025: What's Changed</h2>
        <p>Instagram's 2025 algorithm prioritizes engagement over follower count, video content over static images, and authentic interaction over scheduled posts. Understanding these changes is crucial for growth.</p>
        
        <h2>The Content Formula</h2>
        <p>Successful Instagram growth requires posting 5-7 times per week with this mix: 40% educational content, 30% entertainment, 20% promotional, 10% personal/behind-the-scenes. AI maintains this balance automatically.</p>
        
        <h2>Reels: The Growth Accelerator</h2>
        <p>Reels receive 67% more engagement than regular posts. AI-generated Reels using RunwayML Gen-3 produce professional videos that capture attention, convey messages clearly, and drive profile visits.</p>
        
        <h2>Hashtag Strategy 2025</h2>
        <p>The hashtag game has evolved. Use 15-20 hashtags mixing high-volume (500K+), medium (50K-500K), and niche (5K-50K) terms. AI analyzes which hashtags drive actual engagement for your specific content and audience.</p>
        
        <h2>Engagement Automation</h2>
        <p>While you can't automate liking and commenting (Instagram bans this), AI can identify optimal posting times, respond to comments and DMs, and create engaging content that naturally drives interaction.</p>
        
        <h2>Story Strategy</h2>
        <p>Stories keep you top-of-mind. Post 3-5 stories daily featuring polls, questions, behind-the-scenes, user-generated content, and product showcases. AI schedules and posts these consistently.</p>
        
        <h2>Collaboration and Cross-Promotion</h2>
        <p>AI identifies similar accounts for potential collaborations, suggests cross-promotion opportunities, and even drafts outreach messages. Collaborations typically bring 200-500 new engaged followers per partnership.</p>
        
        <h2>Converting Followers to Customers</h2>
        <p>Growth means nothing without conversion. AI ensures every 5th post includes a clear call-to-action, tracks which content drives website visits, and optimizes your entire funnel from follower to customer.</p>
      `
    },
    {
      id: 9,
      slug: "e-commerce-seo-complete-guide",
      title: "E-Commerce SEO Masterclass: Rank Your Products Higher & Sell More",
      excerpt: "E-commerce SEO is different from regular SEO. Discover the specific optimization strategies that get product pages ranking, attract buyers (not browsers), and turn search traffic into sales.",
      author: "Kavita Shah",
      date: "December 22, 2024",
      readTime: "11 min read",
      category: "E-Commerce",
      image: "from-green-500 to-teal-500",
      content: `
        <h2>E-Commerce SEO: The Fundamentals</h2>
        <p>E-commerce sites face unique SEO challenges: thousands of product pages, duplicate content issues, thin product descriptions, and intense competition. Traditional SEO approaches don't work—you need specialized strategies.</p>
        
        <h2>Product Page Optimization</h2>
        <p>Each product page needs unique, compelling descriptions (minimum 300 words), high-quality images with ALT text, customer reviews, detailed specifications, related product suggestions, and clear calls-to-action. AI creates this at scale.</p>
        
        <h2>Category Page Strategy</h2>
        <p>Category pages often rank better than product pages for broad keywords. Optimize with rich descriptions (500+ words), filtering options, featured products, and internal linking to top-selling items. AI structures this automatically.</p>
        
        <h2>Technical SEO for E-Commerce</h2>
        <ul>
          <li><strong>Site Speed:</strong> Under 3 seconds load time (40% of users abandon slower sites)</li>
          <li><strong>Mobile Optimization:</strong> 70% of e-commerce traffic is mobile</li>
          <li><strong>Structured Data:</strong> Product schema for rich snippets and shopping results</li>
          <li><strong>URL Structure:</strong> Clean, keyword-rich URLs for all products and categories</li>
        </ul>
        
        <h2>Content Marketing for Products</h2>
        <p>Beyond product pages, create buying guides, comparison articles, how-to content, and use-case scenarios. These rank for informational keywords and guide users toward purchase decisions.</p>
        
        <h2>Link Building for E-Commerce</h2>
        <p>Product link building differs from regular SEO. Focus on product reviews, gift guides, "best of" lists, comparison sites, and blogger partnerships. AI identifies opportunities and automates outreach.</p>
        
        <h2>Seasonal Optimization</h2>
        <p>E-commerce is seasonal. AI prepares seasonal content 60-90 days in advance, optimizes for seasonal keywords, and creates holiday-specific landing pages automatically.</p>
        
        <h2>Conversion Rate Optimization</h2>
        <p>Ranking is worthless without sales. AI tests different product descriptions, image arrangements, and CTA placements, continuously optimizing for maximum conversion rates.</p>
      `
    },
    {
      id: 10,
      slug: "future-of-digital-marketing-ai",
      title: "The Future of Digital Marketing: How AI Will Change Everything by 2030",
      excerpt: "Looking ahead: The convergence of AI, automation, and marketing is creating unprecedented opportunities. Explore the trends, technologies, and strategies that will define digital marketing's next era.",
      author: "Dr. Suresh Menon",
      date: "December 20, 2024",
      readTime: "15 min read",
      category: "Future Trends",
      image: "from-indigo-500 to-purple-500",
      content: `
        <h2>The AI Marketing Revolution</h2>
        <p>We're at an inflection point. The next five years will bring more change to digital marketing than the previous twenty. AI isn't just assisting marketers—it's fundamentally reimagining what marketing can be.</p>
        
        <h2>Hyper-Personalization at Scale</h2>
        <p>By 2030, every piece of marketing content will be personalized—not just to segments, but to individuals. AI will generate unique landing pages, emails, and ad creatives for each prospect based on their behavior, preferences, and predicted needs.</p>
        
        <h2>Predictive Marketing</h2>
        <p>AI will predict customer needs before they articulate them. Imagine knowing a customer will need your product in three weeks and starting the nurture sequence now. This isn't science fiction—the technology exists today.</p>
        
        <h2>Voice and Visual Search Dominance</h2>
        <p>Text search will become a minority behavior. By 2030, 60% of searches will be voice or visual. AI optimization will focus on conversational queries and image recognition rather than traditional keywords.</p>
        
        <h2>Autonomous Marketing Systems</h2>
        <p>Complete marketing departments will run autonomously. AI will handle strategy, execution, optimization, and reporting without human intervention. Marketers will shift to oversight, creative direction, and strategic planning.</p>
        
        <h2>The Death of Cookies and Rise of First-Party Data</h2>
        <p>With third-party cookies gone, AI will leverage first-party data more intelligently, predicting behavior from minimal inputs and creating detailed customer profiles from limited information.</p>
        
        <h2>AR/VR Integration</h2>
        <p>Marketing will extend into virtual worlds. AI will create personalized VR showrooms, AR try-before-you-buy experiences, and immersive brand experiences automatically for each customer.</p>
        
        <h2>Ethical AI and Transparency</h2>
        <p>Regulation will mandate AI transparency. Customers will know when AI interacts with them, leading to a new era of ethical AI marketing where authenticity and transparency become competitive advantages.</p>
        
        <h2>The Human Element</h2>
        <p>Ironically, as AI handles execution, human creativity becomes more valuable. The future belongs to marketers who can guide AI strategy, understand customer psychology, and create brand narratives that resonate emotionally.</p>
        
        <h2>Preparing for 2030</h2>
        <p>Start now. Implement AI automation today, build first-party data strategies, experiment with new technologies, and develop skills in AI direction rather than execution. The future is already here—it's just not evenly distributed yet.</p>
      `
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading blogs...</p>
        </div>
      </div>
    );
  }

  const selectedBlog = slug ? blogPosts.find(blog => blog.slug === slug) : null;

  if (selectedBlog) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
        {/* Navigation */}
        <nav className="navbar px-6 py-4 bg-white/80 backdrop-blur-xl border-b sticky top-0 z-50">
          <div className="container mx-auto flex justify-between items-center">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-11 h-11 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                A
              </div>
              <span className="text-2xl font-bold heading bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AutomateSEO.ai</span>
            </div>
            <Button variant="ghost" onClick={() => navigate('/blogs')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blogs
            </Button>
          </div>
        </nav>

        {/* Blog Content */}
        <article className="container mx-auto px-6 py-12 max-w-4xl">
          <div className="mb-8">
            <div className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
              {selectedBlog.category}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold heading mb-4">{selectedBlog.title}</h1>
            <div className="flex flex-wrap items-center gap-6 text-gray-600">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>{selectedBlog.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{selectedBlog.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{selectedBlog.readTime}</span>
              </div>
            </div>
          </div>

          <div 
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: selectedBlog.content }}
            style={{
              lineHeight: '1.8',
              fontSize: '1.125rem'
            }}
          />

          <div className="mt-12 pt-8 border-t">
            <Button onClick={() => navigate('/register')} size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600">
              Start Your Free Trial
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </article>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Navigation */}
      <nav className="navbar px-6 py-4 bg-white/80 backdrop-blur-xl border-b sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-11 h-11 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
              A
            </div>
            <span className="text-2xl font-bold heading bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AutomateSEO.ai</span>
          </div>
          <Button onClick={() => navigate('/register')} className="bg-gradient-to-r from-blue-600 to-purple-600">
            Start Free Trial
          </Button>
        </div>
      </nav>

      {/* Blog List */}
      <div className="container mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold heading mb-4">Marketing Automation Insights</h1>
          <p className="text-xl text-gray-600">Expert guides, strategies, and trends in AI-powered marketing</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {blogPosts.map((blog) => (
            <Card key={blog.id} className="card group cursor-pointer" onClick={() => navigate(`/blog/${blog.slug}`)}>
              <CardHeader>
                <div className={`w-full h-48 bg-gradient-to-br ${blog.image} rounded-xl mb-4 flex items-center justify-center text-white text-6xl font-bold group-hover:scale-105 transition-transform`}>
                  {blog.id}
                </div>
                <div className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium mb-2">
                  {blog.category}
                </div>
                <CardTitle className="text-xl group-hover:text-blue-600 transition">{blog.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base mb-4">{blog.excerpt}</CardDescription>
                <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span>{blog.author}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>{blog.readTime}</span>
                  </div>
                </div>
                <Button variant="ghost" className="w-full group-hover:bg-blue-50">
                  Read More
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BlogsPage;
